/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
// format-number-inputs.directive.ts
import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
    selector: '[formatNumberInput]',
    standalone: false
})

export class FormatNumberInputDirective {
  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  // This function is triggered on 'input' event
  formatNumberInput(event: any) {
    // Get a reference to the input element
    const inputElement = this.el.nativeElement;

    // Store the current cursor position
    const cursorPosition = inputElement.selectionStart;

    // Store the current value for later comparison
    const oldValue = inputElement.value;

    // counts the amount of commas after the decimal point as these will be removed in the formatting process
    const commasToBeRemoved = countCommasAfterDecimal(oldValue);
    // counts the amount of commas to add to the value if the value is a whole number
    const commasToBeAdded = countCommasToAdd(oldValue);

    // Format the input value with commas as thousand separators
    let formattedValue = formatInputWithCommas(inputElement.value);

    // Update the input value
    inputElement.value = formattedValue;

    // Calculate the difference in length between the old and new values after removing commas after the decimal from the old value
    const lengthDiff = formattedValue.length - (oldValue.length - commasToBeRemoved + commasToBeAdded);

    // Adjust the cursor position based on the difference in length
    inputElement.setSelectionRange(cursorPosition + lengthDiff, cursorPosition + lengthDiff);
  }
}

// Function to format a string with commas as thousand separators
export function formatInputWithCommas(value: string): string {

  if(value === null || value === undefined) return;

  // Check if the value ends with a "."
  let endsWithDot = value.endsWith('.');
  // Check if the value starts with a "-"
  const isNegative = value.startsWith('-');
  // Split the value into whole and decimal parts
  let [whole, decimal] = value.split('.');
  // Remove all non-digit characters from the whole part
  whole = whole.replace(/\D/g, '');
  // If there is no whole part and there is a decimal part, then the whole part should be 0
  if (whole === '' && (decimal || endsWithDot)) {
    whole = '0';
  }
  if (decimal) {
    // Remove non-digit characters from the decimal part
    decimal = decimal.replace(/\D/g, '');
    // If there was a decimal part and it was removed, then the value should end with a "."
    endsWithDot = !decimal;
  }
  // Format the whole part with commas
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // If the value was negative, add the "-" back
  const formattedValue = isNegative ? '-' + formattedWhole : formattedWhole;
  // If there was a decimal part, add it back
  let result = decimal ? formattedValue + '.' + decimal : formattedValue;
  // If the value ended with a ".", add it back
  return endsWithDot ? result + '.' : result;
}

function countCommasAfterDecimal(value: string): number {
  const parts = value.split('.');
  if (parts.length < 2) {
    return 0; // No decimal part
  }
  const decimalPart = parts[1];
  return (decimalPart.match(/,/g) || []).length;
}

function countCommasToAdd(value: string): number {
  //if value doesnt contain a . then return 0
  if (value.includes('.')) return 0;

  // Get the number of commas in the old value
  const oldCommas = (value.match(/,/g) || []).length;

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Calculate how many commas will be added
  // Commas are added for every three digits, starting from the right
  // So, if there are less than four digits, no commas will be added
  // If there are n digits, the number of commas added will be floor((n - 1) / 3)
  const newCommas = Math.floor((digits.length - 1) / 3);

  let commasToAdd = newCommas - oldCommas;

  // If adding one or fewer commas doesn't need to be handled here
  return commasToAdd <= 1 ? 0 : commasToAdd - 1;
}




